'use client'

import { Suspense, useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { getMyCIOClient } from '@/lib/mycio/supabase-mycio'
import { identifyMyCIOUser, resetAnalytics, track } from '@/lib/analytics/mycio'
import { CONSENT_EVENT, analyticsConsentGranted } from '@/lib/analytics/consent'
import { captureReferralOnLand, primeReferralToken, clearReferralToken } from '@/lib/mycio/referral'

/**
 * PostHog bootstrap for the whole app (instrumentation-brief.md).
 *  - Single guarded init (React strict-mode / remount safe) — posthog.__loaded gates re-init.
 *  - Reverse-proxied through our own origin (`/ingest`, see next.config rewrites) so ad-blockers don't
 *    drop events AND so we don't need a posthog host in the CSP connect-src (same-origin).
 *  - Manual $pageview on app-router route changes (autocapture misses SPA navigation).
 *  - Session replay WITH financial-data masking (never record $ amounts / inputs).
 *  - Identity bridge: identify() on SIGNED_IN stitches the anonymous /instant session to the account;
 *    reset() on SIGNED_OUT starts a fresh anonymous id.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    if (!key) return
    if (!(posthog as unknown as { __loaded?: boolean }).__loaded) {
      posthog.init(key, {
        // Same-origin proxy (next.config rewrites /ingest → PostHog). ui_host keeps toolbar/links correct.
        api_host: '/ingest',
        ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false, // we emit $pageview manually below (app-router route changes)
        capture_pageleave: true,
        respect_dnt: true,
        // Replay privacy: this is a financial product, so MASK ALL TEXT, not a hand-maintained selector
        // list. maskAllInputs only covers <input>/<textarea>; the plan renders the user's real money
        // figures as TEXT nodes, and no element carries a mask class — so a selector-based mask would
        // record actual amounts. maskTextSelector:'*' masks every text node (replay keeps DOM/interaction
        // shape, never readable values). posthog-js has no maskAllText — '*' is the documented all-text mask.
        // Do NOT loosen this without a per-element allowlist review.
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
        },
        // NOTHING CAPTURES BEFORE CONSENT — not replay, not autocapture, not pageviews. The old
        // shape gated only session recording and let every event run from first paint while the
        // privacy page said no analytics cookies exist; the 2026-09 observability review called it
        // correctly. opt_out by default; CookieConsent's answer flips it below. `respect_dnt`
        // stays: a browser-level no is a no even after a banner yes.
        autocapture: false,
        opt_out_capturing_by_default: true,
        // No ph_* cookie/localStorage BEFORE consent either: capture-blocking alone still wrote
        // the distinct_id + opt-out state for every visitor at init, while the privacy page says
        // ph_* is set only if analytics is allowed (2026-09-04 review, HIGH — the claim must be
        // true, and the config fix is better than weakening the claim).
        opt_out_persistence_by_default: true,
        disable_session_recording: true,
      })
    }
    // Identity seen before consent existed: identifyMyCIOUser refuses while unconsented, so a
    // sign-in that precedes the banner answer would otherwise stay anonymous for the whole
    // session even after the user accepts. applyConsent retries it on grant.
    let identifiedUid: string | null = null
    let identifiedEmail: string | undefined
    const applyConsent = () => {
      if (!(posthog as unknown as { __loaded?: boolean }).__loaded) return
      if (analyticsConsentGranted()) {
        if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing()
        posthog.startSessionRecording()
        if (identifiedUid) identifyMyCIOUser(identifiedUid, { email: identifiedEmail })
      } else {
        // declined OR undecided: both mean no capture. opt_out also stops queued events.
        if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing()
        posthog.stopSessionRecording()
      }
    }
    applyConsent()
    window.addEventListener(CONSENT_EVENT, applyConsent)

    const sb = getMyCIOClient()
    // Stitch identity on any auth completion (OTP / magic-link / password — one central bridge). Dedupe by
    // uid so a frequent TOKEN_REFRESHED doesn't re-$set on every refresh; reset() on sign-out clears it so a
    // different user next signs in cleanly (posthog would otherwise refuse to re-identify without a reset).
    const { data: sub } = sb.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        identifiedUid = null
        identifiedEmail = undefined
        resetAnalytics()
        clearReferralToken() // the token is per-user — don't let the next user reuse it
        return
      }
      const uid = session?.user?.id
      if (uid && uid !== identifiedUid) {
        // A direct A→B account switch can emit SIGNED_IN(B) with no SIGNED_OUT — reset first so PostHog
        // doesn't refuse the re-identify and land B's events on A's person.
        if (identifiedUid && identifiedUid !== uid) {
          resetAnalytics()
          clearReferralToken()
        }
        identifiedUid = uid
        identifiedEmail = session?.user?.email ?? undefined
        identifyMyCIOUser(uid, { email: identifiedEmail })
        // Prime the signed referral token so this user's share links attribute (C1).
        void primeReferralToken(session?.access_token)
      }
    })
    return () => {
      sub.subscription.unsubscribe()
      window.removeEventListener(CONSENT_EVENT, applyConsent)
    }
  }, [])

  return (
    <>
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      {children}
    </>
  )
}

/**
 * Redact secret capability tokens from a path before it's sent to PostHog. /plan/[token] is a PUBLIC,
 * unguessable share URL that returns the full financial plan with no auth — leaking the token as a
 * $current_url event property would let anyone with PostHog read access open the shared plan. Text masking
 * does NOT cover event properties, so this must happen here. Collapses /plan/<token> → /plan/[token].
 */
function redactTokens(pathname: string): string {
  return pathname.replace(/^\/plan\/[^/]+/, '/plan/[token]')
}

/**
 * Emits a $pageview on every app-router path/query change. useSearchParams() forces this into its own
 * Suspense boundary (Next requirement) so it never opts the whole tree into client rendering.
 */
function PageviewTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!pathname) return
    const safePath = redactTokens(pathname)
    // A referred visitor lands with ?ref=&loop= — persist it + fire invite_clicked (idempotent).
    captureReferralOnLand()
    // Query string is dropped from the reported URL: it can carry the same token / utm / email params.
    // source: the channel we care about for page_view{source}; PostHog also sends the (redacted) $current_url.
    // `ref` is a SIGNED, unforgeable referral token (referral.ts) — the URL is redacted above and
    // the token must not re-enter through an event property. The channel is what analytics needs.
    track('page_view', { source: searchParams?.get('utm_source') || (searchParams?.get('ref') ? 'referral' : undefined) })
    if (typeof window !== 'undefined' && (posthog as unknown as { __loaded?: boolean }).__loaded) {
      posthog.capture('$pageview', { $current_url: window.location.origin + safePath })
    }
  }, [pathname, searchParams])

  return null
}
